import SwapHorizRounded from '@mui/icons-material/SwapHorizRounded';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { api, unwrap } from '../../../api/client';
import { money } from '../../../lib/format';
import { useNotify } from '../../../lib/notify';
import { useShop } from '../../../shop/ShopProvider';
import {
  autoAllocate,
  draftToLines,
  fromUnits,
  isAllocatable,
  summarize,
  toUnits,
  type AllocationDraft,
} from '../allocation';
import { AllocationEditor, type EditorItem } from './AllocationEditor';
import { idempotency, invalidateMoney, useOpenItems, type Allocation } from './hooks';
import { docType, type Ledger } from './labels';

export type CreditType = 'PAYMENT' | 'OPENING_BALANCE' | 'CREDIT_NOTE' | 'DEBIT_NOTE';

/**
 * Manual reallocation (ACCOUNTING_RULES §4.4): undo some of this credit's allocations and settle
 * other documents instead. Balances never change — only which document the money settles.
 */
export function ReallocateDialog({
  open,
  onClose,
  contactId,
  ledger,
  credit,
  allocations,
  unallocated,
}: {
  open: boolean;
  onClose: () => void;
  contactId: string;
  ledger: Ledger;
  credit: { type: CreditType; id: string; number: string };
  allocations: readonly Allocation[];
  unallocated: string;
}) {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const { format } = useShop();
  const d = format.currencyDecimals;
  const notify = useNotify();
  const qc = useQueryClient();
  const openItems = useOpenItems(open ? contactId : null, ledger);

  const active = useMemo(
    () =>
      allocations.filter(
        (a) => !a.reversed && a.credit.id === credit.id && isAllocatable(a.target.type),
      ),
    [allocations, credit.id],
  );
  const [undo, setUndo] = useState<Set<string>>(new Set());
  const [draft, setDraft] = useState<AllocationDraft>({});
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);

  const undone = active.filter((a) => undo.has(a.id));
  const available = fromUnits(
    (toUnits(unallocated) ?? 0n) + undone.reduce((s, a) => s + (toUnits(a.amount) ?? 0n), 0n),
    d,
  );

  // Open documents, with what undoing allocations would re-open added back.
  const items: EditorItem[] = (() => {
    const claims = (openItems.data?.claims ?? []).filter((c) => isAllocatable(c.type));
    const map = new Map<string, EditorItem>(claims.map((c) => [c.id, { ...c }]));
    for (const a of undone) {
      const existing = map.get(a.target.id);
      if (existing) {
        existing.open = fromUnits((toUnits(existing.open) ?? 0n) + (toUnits(a.amount) ?? 0n), d);
      } else {
        map.set(a.target.id, {
          type: a.target.type,
          id: a.target.id,
          number: a.target.number,
          date: '',
          dueDate: null,
          amount: a.amount,
          open: a.amount,
          overdue: false,
        });
      }
    }
    return [...map.values()];
  })();

  const summary = summarize(available, draft, items);
  const lines = draftToLines(draft, items, d);
  const reasonError = touched && reason.trim().length < 3;
  const nothingToDo = undo.size === 0 && lines.length === 0;

  const reset = () => {
    setUndo(new Set());
    setDraft({});
    setReason('');
    setTouched(false);
  };

  const save = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/allocations/reallocate', {
          params: idempotency(),
          body: {
            contactId,
            ledger,
            reason: reason.trim(),
            reverse: [...undo],
            allocate: lines.map((l) => ({
              credit: { type: credit.type, id: credit.id },
              target: {
                type: l.item.type as 'SALES_INVOICE' | 'SUPPLIER_INVOICE' | 'OPENING_BALANCE',
                id: l.item.id,
              },
              amount: l.amount,
            })),
          },
        }),
      ),
    onSuccess: async () => {
      notify.success('Allocations updated');
      await invalidateMoney(qc);
      reset();
      onClose();
    },
    onError: (e) => notify.error(e),
  });

  const close = () => {
    if (save.isPending) return;
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="md" fullScreen={phone}>
      <DialogTitle sx={{ fontWeight: 700 }}>
        <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
          <SwapHorizRounded color="primary" />
          <span>Reallocate {credit.number}</span>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Choose which documents this {docType(credit.type).toLowerCase()} settles. Balances don’t
          change — only where the money is applied.
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ px: 0 }}>
        {active.length > 0 && (
          <Box sx={{ px: 3, mb: 2 }}>
            <Typography variant="overline" color="text.secondary">
              Currently settles — tick to undo
            </Typography>
            <Stack sx={{ mt: 0.5 }}>
              {active.map((a) => (
                <FormControlLabel
                  key={a.id}
                  control={
                    <Checkbox
                      checked={undo.has(a.id)}
                      onChange={(_, v) =>
                        setUndo((prev) => {
                          const next = new Set(prev);
                          if (v) next.add(a.id);
                          else next.delete(a.id);
                          return next;
                        })
                      }
                    />
                  }
                  label={
                    <Typography variant="body2">
                      <b>{a.target.number ?? docType(a.target.type)}</b>{' '}
                      <Box component="span" sx={{ color: 'text.secondary' }}>
                        {docType(a.target.type)} ·{' '}
                      </Box>
                      <span className="tabular">{money(a.amount, format)}</span>
                    </Typography>
                  }
                />
              ))}
            </Stack>
          </Box>
        )}
        <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ px: 3, pt: 2, display: 'block' }}
          >
            Settle these instead
          </Typography>
          <AllocationEditor
            amount={available}
            items={items}
            draft={draft}
            loading={openItems.isLoading}
            onEdit={(id, value) => setDraft((prev) => ({ ...prev, [id]: value }))}
            onAuto={() => setDraft(autoAllocate(available, items, d))}
            onClear={() => setDraft({})}
            creditLabel="Left as credit"
            emptyText="No open documents for this party. Undo an allocation above to move it."
          />
        </Box>
        <Box sx={{ px: 3, pt: 2.5 }}>
          {summary.overAllocated && (
            <Alert severity="error" sx={{ mb: 2 }}>
              You’re allocating more than the {money(available, format)} available.
            </Alert>
          )}
          <TextField
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onBlur={() => setTouched(true)}
            fullWidth
            error={reasonError}
            helperText={reasonError ? 'Give a short reason' : 'Kept in the audit trail.'}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button variant="outlined" onClick={close} disabled={save.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          loading={save.isPending}
          disabled={nothingToDo || !summary.valid}
          onClick={() => {
            setTouched(true);
            if (reason.trim().length < 3) return;
            save.mutate();
          }}
        >
          Save allocations
        </Button>
      </DialogActions>
    </Dialog>
  );
}
